<template>
  <div class="suggestions-page space-y-5">
    <!-- Header -->
    <div class="flex items-start justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tracking-tight">Suggested collections</h1>
        <p class="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5 flex items-center gap-2 flex-wrap">
          <span v-if="summary && summary.generatedAt">Generated {{ generatedLabel }}</span>
          <span v-else>No run yet</span>
          <span>&middot; {{ rows.length }} open</span>
          <span v-if="overdueCount">&middot; {{ overdueCount }} overdue<template v-if="grouped.lapsed.length"> ({{ grouped.lapsed.length }} lapsed)</template></span>
          <span v-if="summary && summary.source" :class="['inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold',
              summary.source === 'claude' ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300' : 'bg-amber-50 text-amber-700']">
            <span class="material-icons text-[13px]" aria-hidden="true">{{ summary.source === 'claude' ? 'auto_awesome' : 'calculate' }}</span>
            {{ summary.source === 'claude' ? 'AI ranked' : 'Engine only (AI unavailable)' }}
          </span>
        </p>
      </div>
      <div class="flex items-center gap-2.5">
        <button v-if="selected.size && (tab === 'OPEN' || tab === 'LAPSED')" @click="acceptSelected" :disabled="bulkBusy"
          class="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-700 text-white hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
          <span class="material-icons text-[18px]" aria-hidden="true">check_circle</span>
          Accept selected ({{ selected.size }})
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex gap-1 border-b border-neutral-200 dark:border-neutral-700">
      <button v-for="t in tabs" :key="t.key" @click="tab = t.key"
        :class="['px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
          tab === t.key ? 'border-primary-600 text-primary-700 dark:text-primary-300' : 'border-transparent text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200']">
        {{ t.label }}<span v-if="t.count != null" class="ml-1.5 text-xs text-neutral-400">{{ t.count }}</span>
      </button>
    </div>

    <!-- Loading / error / empty -->
    <div v-if="loading" class="text-sm text-neutral-500 py-8 text-center">Loading suggestions…</div>
    <div v-else-if="error" class="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm px-4 py-3 flex items-center justify-between gap-3">
      <span>{{ error }}</span>
      <button @click="load" class="px-3 py-1.5 rounded-md text-xs font-semibold bg-neutral-100 text-neutral-800 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-100">Retry</button>
    </div>

    <!-- OPEN tab -->
    <template v-else-if="tab === 'OPEN'">
      <div v-if="!rows.length" class="text-sm text-neutral-500 py-10 text-center">Nothing suggested right now. Check back after tomorrow's run.</div>
      <section v-if="grouped.overdue.length" class="space-y-2">
        <h2 class="text-xs font-semibold uppercase tracking-wider text-red-700 dark:text-red-300">Overdue ({{ grouped.overdue.length }})</h2>
        <SuggestionRow v-for="r in grouped.overdue" :key="r.id" :row="r" :selected="selected.has(r.id)"
          @toggle="toggleSelect(r.id)"
          :busy="busyIds.has(r.id)"
          @accept="accept(r)" @edit="openEdit(r)" @dismiss="openDismiss(r)" @lost="openLost(r)" />
      </section>
      <section v-for="g in grouped.days" :key="g.day" class="space-y-2">
        <h2 class="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{{ g.label }} ({{ g.items.length }})</h2>
        <SuggestionRow v-for="r in g.items" :key="r.id" :row="r" :selected="selected.has(r.id)"
          @toggle="toggleSelect(r.id)"
          :busy="busyIds.has(r.id)"
          @accept="accept(r)" @edit="openEdit(r)" @dismiss="openDismiss(r)" @lost="openLost(r)" />
      </section>
    </template>

    <!-- LAPSED tab: long-dormant overdue points (flag LAPSED) — same rows, Mark-as-lost is the expected action -->
    <template v-else-if="tab === 'LAPSED'">
      <div v-if="!grouped.lapsed.length" class="text-sm text-neutral-500 py-10 text-center">No lapsed customers.</div>
      <section v-else class="space-y-2">
        <p class="text-sm text-neutral-500 dark:text-neutral-400">Overdue by more than twice their usual gap, or not collected for over 90 days. Accept if they are still active; otherwise mark them as lost so they stop appearing.</p>
        <SuggestionRow v-for="r in grouped.lapsed" :key="r.id" :row="r" :selected="selected.has(r.id)"
          @toggle="toggleSelect(r.id)"
          :busy="busyIds.has(r.id)"
          @accept="accept(r)" @edit="openEdit(r)" @dismiss="openDismiss(r)" @lost="openLost(r)" />
      </section>
    </template>

    <!-- ACCEPTED / DISMISSED tabs -->
    <template v-else-if="tab === 'ACCEPTED' || tab === 'DISMISSED'">
      <div v-if="!historyRows.length" class="text-sm text-neutral-500 py-10 text-center">None yet.</div>
      <div v-else class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700">
        <div v-for="r in historyRows" :key="r.id" class="px-4 py-3 text-sm flex items-center gap-3 flex-wrap">
          <span class="font-medium text-neutral-900 dark:text-neutral-100 flex-1 min-w-[12rem] truncate">{{ pointLabel(r) }}</span>
          <span class="text-neutral-500">{{ dayLabel(datePart(r.suggestedDate)) }}</span>
          <span v-if="tab === 'ACCEPTED'" class="text-neutral-500">by {{ r.acceptedBy }}</span>
          <span v-else class="text-neutral-500">{{ dismissLabel(r.dismissReason) }} · by {{ r.dismissedBy }}</span>
          <router-link v-if="r.collectionRequestId" :to="{ name: 'OrderView', params: { id: r.collectionRequestId, supplierId: r.supplierId } }"
            class="text-primary-700 dark:text-primary-300 hover:underline text-xs">open request</router-link>
        </div>
      </div>
    </template>

    <!-- LOST tab -->
    <template v-else-if="tab === 'LOST'">
      <div v-if="!lostRows.length" class="text-sm text-neutral-500 py-10 text-center">No customers marked lost.</div>
      <div v-else class="bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-700">
        <div v-for="p in lostRows" :key="p.pointId" class="px-4 py-3 text-sm flex items-center gap-3 flex-wrap">
          <span class="font-medium text-neutral-900 dark:text-neutral-100 flex-1 min-w-[12rem] truncate">{{ pointLabel(p) }}</span>
          <span class="text-neutral-500" v-if="p.changedAt">marked {{ dayLabel(dubaiDayStr(p.changedAt)) }} by {{ p.changedBy }}</span>
          <router-link :to="p.locationId !== '0'
              ? { name: 'LocationView', params: { supplierId: p.supplierId, locationId: p.locationId } }
              : { name: 'SupplierView', params: { id: p.supplierId } }"
            class="text-primary-700 dark:text-primary-300 hover:underline text-xs">Reactivate on supplier page</router-link>
        </div>
      </div>
    </template>

    <CollecionRequestModal v-if="editing" :showForm="true" :prefill="editPrefill"
      @close="editing = null" @created="onEditedCreated" />

    <!-- Dismiss reason picker -->
    <div v-if="dismissing" class="fixed inset-0 z-40 flex items-center justify-center bg-black/40" @click.self="dismissing = null">
      <div class="bg-white dark:bg-neutral-800 rounded-xl shadow-lg border border-neutral-200 dark:border-neutral-700 w-full max-w-sm p-5 space-y-3" role="dialog" aria-modal="true" aria-label="Dismiss suggestion">
        <h3 class="font-semibold text-neutral-900 dark:text-neutral-100">Dismiss {{ pointLabel(dismissing) }}</h3>
        <p class="text-sm text-neutral-500">It will be suggested again after its usual interval.</p>
        <div class="flex flex-wrap gap-2">
          <button v-for="opt in dismissOptions" :key="opt.value" @click="dismiss(dismissing, opt.value)" :disabled="busyIds.has(dismissing.id)"
            class="px-3 py-1.5 rounded-full text-sm bg-neutral-100 hover:bg-neutral-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 text-neutral-800 dark:text-neutral-100 disabled:opacity-50">{{ opt.label }}</button>
        </div>
        <div class="text-right"><button @click="dismissing = null" class="text-sm text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200">Cancel</button></div>
      </div>
    </div>

    <DeleteRecordDialog v-if="losing" :showModal="true" :loading="lostBusy"
      title="Mark customer as lost?"
      :body="`${pointLabel(losing)} will no longer be suggested. You can reactivate it from the supplier page (Collection status: Active).`"
      confirm-label="Mark as lost"
      @confirm="confirmLost" @cancel="losing = null" />
  </div>
</template>

<script>
import { computed, h, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import useSuggestionsApi from "@/composables/useSuggestionsApi";
import useSuggestionsSummary from "@/composables/useSuggestionsSummary";
import { groupOpenSuggestions, pointLabel, confidenceClass, flagLabel, flagClass } from "@/utils/suggestionGroups";
import { datePart, dayLabel, dubaiDayStr, dubaiTodayStr } from "@/utils/dubaiDay";
import DeleteRecordDialog from "@/components/DeleteRecordDialog.vue";
import CollecionRequestModal from "@/components/CollecionRequestModal.vue";
import useToast from "@/composables/useToast";

const DISMISS_LABELS = {
  NOT_NEEDED_YET: "Not needed yet", CUSTOMER_SKIP: "Customer asked to skip",
  COLLECTED_OUTSIDE_OMS: "Collected outside OMS", OTHER: "Other", LOST: "Marked lost",
};

// One suggestion. Render function keeps the row self-contained; actions are emitted
// and wired up by the page (Task 3).
const SuggestionRow = {
  name: "SuggestionRow",
  props: { row: Object, selected: Boolean, busy: Boolean },
  emits: ["toggle", "accept", "edit", "dismiss", "lost"],
  setup(props, { emit }) {
    const open = ref(false);
    const btn = (label, cls, ev, disabled = false) =>
      h("button", { type: "button", disabled: disabled || props.busy, onClick: () => emit(ev),
        class: `px-2.5 py-1 rounded-md text-xs font-semibold transition-colors disabled:opacity-50 ${cls}` }, label);
    return () => {
      const r = props.row;
      const e = r.evidence || {};
      return h("div", { class: "bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 px-4 py-3" }, [
        h("div", { class: "flex items-start gap-3" }, [
          h("input", { type: "checkbox", checked: props.selected, onChange: () => emit("toggle"),
            class: "mt-1 rounded border-neutral-300 text-primary-600 focus:ring-primary-500", "aria-label": `Select ${pointLabel(r)}` }),
          h("div", { class: "flex-1 min-w-0" }, [
            h("div", { class: "flex items-center gap-2 flex-wrap" }, [
              h("span", { class: "font-semibold text-neutral-900 dark:text-neutral-100 truncate" }, pointLabel(r)),
              h("span", { class: "text-sm text-neutral-600 dark:text-neutral-300" }, datePart(r.suggestedDate) === dubaiTodayStr() ? "Today" : dayLabel(datePart(r.suggestedDate))),
              h("span", { class: "text-sm text-neutral-500" }, `~${Math.round(r.estimatedVolume || 0)} L`),
              h("span", { class: `px-2 py-0.5 rounded-full text-[11px] font-semibold ${confidenceClass(r.confidence)}` }, r.confidence),
              r.overdue && h("span", { class: `px-2 py-0.5 rounded-full text-[11px] font-semibold ${flagClass("OVERDUE")}` }, `Overdue ${r.daysOverdue}d`),
              ...(r.flags || []).filter((f) => f !== "OVERDUE").map((f) =>
                h("span", { key: f, class: `px-2 py-0.5 rounded-full text-[11px] font-semibold ${flagClass(f)}` }, flagLabel(f))),
            ]),
            h("p", { class: "text-sm text-neutral-600 dark:text-neutral-400 mt-1" }, r.rationale),
            h("div", { class: "flex items-center gap-2 mt-2 flex-wrap" }, [
              btn("Accept", "bg-emerald-700 text-white hover:bg-emerald-800", "accept"),
              btn("Edit…", "bg-neutral-100 text-neutral-800 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-100", "edit"),
              btn("Dismiss", "bg-neutral-100 text-neutral-800 hover:bg-neutral-200 dark:bg-neutral-700 dark:text-neutral-100", "dismiss"),
              btn("Mark as lost", "bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-300", "lost"),
              h("button", { type: "button", onClick: () => (open.value = !open.value),
                class: "ml-auto text-xs text-neutral-500 hover:text-neutral-800 dark:hover:text-neutral-200 inline-flex items-center gap-0.5" },
                [h("span", { class: "material-icons text-[14px]", "aria-hidden": "true" }, open.value ? "expand_less" : "expand_more"), "evidence"]),
            ]),
            open.value && h("dl", { class: "mt-3 grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-400 border-t border-neutral-100 dark:border-neutral-700 pt-2" }, [
              ...[
                ["Collections (12 mo)", e.collections],
                ["Typical gap", e.medianGapDays != null ? `${e.medianGapDays} days` : "—"],
                ["Gap variation", e.gapVariation != null ? `${Math.round(e.gapVariation * 100)}%` : "—"],
                ["Last collected", e.lastCollectedAt ? `${dayLabel(dubaiDayStr(e.lastCollectedAt))} (${e.daysSinceLast}d ago)` : "—"],
                ["Usual weekdays", (e.preferredWeekdays || []).join(", ") || "—"],
                ["Typical volume", e.medianVolume != null ? `${Math.round(e.medianVolume)} L (max ${Math.round(e.maxVolume || 0)})` : "—"],
                ["Volume trend", e.volumeTrend || "—"],
                ["Source", r.source === "claude" ? "AI ranked" : "Engine only"],
              ].flatMap(([k, v]) => [h("dt", { class: "font-medium" }, k), h("dd", null, String(v ?? "—"))]),
              (e.openPastRequestIds || []).length
                ? h("dd", { class: "col-span-full" }, [
                    "Unactioned past requests: ",
                    ...e.openPastRequestIds.map((id) => h("a", { key: id, href: `/dashboard/operations/order-view/${id}/${r.supplierId}`, target: "_blank",
                      class: "text-primary-700 dark:text-primary-300 hover:underline mr-2" }, id.slice(0, 8))),
                  ])
                : null,
              r.dateCorrected ? h("dd", { class: "col-span-full text-amber-700" }, `Date adjusted by rules: ${r.dateCorrected}`) : null,
            ]),
          ]),
        ]),
      ]);
    };
  },
};

export default {
  name: "Suggestions",
  components: { SuggestionRow, DeleteRecordDialog, CollecionRequestModal },
  setup() {
    const store = useStore();
    store.commit("setCurrentRoute", "Suggestions");
    const api = useSuggestionsApi();
    const { summary, refresh: refreshSummary, markSeen } = useSuggestionsSummary(store);

    const rows = ref([]);
    const historyRows = ref([]);
    const lostRows = ref([]);
    const selected = ref(new Set());
    const tab = ref("OPEN");
    const loading = ref(false);
    const error = ref(null);

    const load = async () => {
      loading.value = true;
      error.value = null;
      try {
        if (tab.value === "OPEN" || tab.value === "LAPSED") rows.value = await api.fetchSuggestions("OPEN");
        else if (tab.value === "LOST") lostRows.value = await api.fetchLost();
        else historyRows.value = await api.fetchSuggestions(tab.value);
        await refreshSummary();
        markSeen();
      } catch (err) {
        console.error("Error loading suggestions:", err);
        error.value = "Suggestions could not be loaded. Check your connection and try again.";
      } finally {
        loading.value = false;
      }
    };

    const removeRow = (id) => {
      rows.value = rows.value.filter((r) => r.id !== id);
      const next = new Set(selected.value);
      next.delete(id);
      selected.value = next;
    };
    const toggleSelect = (id) => {
      const next = new Set(selected.value);
      next.has(id) ? next.delete(id) : next.add(id);
      selected.value = next;
    };

    const { showToast } = useToast();
    const busyIds = ref(new Set());
    const bulkBusy = ref(false);
    const dismissing = ref(null);
    const losing = ref(null);
    const lostBusy = ref(false);
    const dismissOptions = [
      { value: "NOT_NEEDED_YET", label: "Not needed yet" },
      { value: "CUSTOMER_SKIP", label: "Customer asked to skip" },
      { value: "COLLECTED_OUTSIDE_OMS", label: "Collected outside OMS" },
      { value: "OTHER", label: "Other" },
    ];
    const setBusy = (id, on) => {
      const next = new Set(busyIds.value);
      on ? next.add(id) : next.delete(id);
      busyIds.value = next;
    };

    const accept = async (r) => {
      setBusy(r.id, true);
      try {
        const res = await api.acceptSuggestion(r.id, {});
        showToast("success", `Collection requested for ${pointLabel(r)} — ${dayLabel(datePart(res.requestedDate))}`);
        removeRow(r.id);
        refreshSummary();
        return true;
      } catch (err) {
        const status = err.response && err.response.status;
        const detail = (err.response && err.response.data && err.response.data.detail) || "Failed to accept the suggestion.";
        showToast(status === 409 ? "info" : "error", detail);
        if (status === 409) removeRow(r.id); // API marked it SUPERSEDED
        return false;
      } finally {
        setBusy(r.id, false);
      }
    };

    const acceptSelected = async () => {
      bulkBusy.value = true;
      const targets = rows.value.filter((r) => selected.value.has(r.id));
      let ok = 0;
      for (const r of targets) {
        if (await accept(r)) ok += 1; // sequential: one request per click keeps 409 handling simple
      }
      bulkBusy.value = false;
      showToast(ok === targets.length ? "success" : "info", `${ok} of ${targets.length} suggestions accepted`);
    };

    const openDismiss = (r) => (dismissing.value = r);
    const dismiss = async (r, reason) => {
      setBusy(r.id, true);
      try {
        await api.dismissSuggestion(r.id, reason);
        showToast("success", `Dismissed ${pointLabel(r)}`);
        removeRow(r.id);
        dismissing.value = null;
        refreshSummary();
      } catch (err) {
        showToast("error", (err.response && err.response.data && err.response.data.detail) || "Failed to dismiss.");
      } finally {
        setBusy(r.id, false);
      }
    };

    const openLost = (r) => (losing.value = r);
    const confirmLost = async () => {
      const r = losing.value;
      lostBusy.value = true;
      try {
        await api.setCollectionStatus(r.supplierId, "lost", r.locationId !== "0" ? parseInt(r.locationId, 10) : null);
        showToast("success", `${pointLabel(r)} marked as lost`);
        removeRow(r.id);
        losing.value = null;
        refreshSummary();
      } catch (err) {
        showToast("error", (err.response && err.response.data && err.response.data.detail) || "Failed to mark as lost.");
      } finally {
        lostBusy.value = false;
      }
    };

    const editing = ref(null);
    const editPrefill = computed(() => editing.value && {
      supplierId: editing.value.supplierId,
      locationId: editing.value.locationId,
      requestedDate: editing.value.suggestedDate,
      estimatedVolume: editing.value.estimatedVolume,
    });
    const openEdit = (r) => (editing.value = r);
    const onEditedCreated = async (createdId) => {
      const r = editing.value;
      editing.value = null;
      if (!r) return;
      if (!createdId) {
        showToast("info", "Request created. The suggestion stays open — dismiss it as 'Collected outside OMS' if needed.");
        return;
      }
      try {
        await api.acceptSuggestion(r.id, { collectionRequestId: createdId });
        showToast("success", `Suggestion for ${pointLabel(r)} linked to the new request`);
        removeRow(r.id);
        refreshSummary();
      } catch (err) {
        showToast("error", (err.response && err.response.data && err.response.data.detail) || "Request created but the suggestion could not be linked.");
      }
    };

    const grouped = computed(() => groupOpenSuggestions(rows.value));
    const overdueCount = computed(() => rows.value.filter((r) => r.overdue).length);
    const generatedLabel = computed(() => {
      const iso = summary.value && summary.value.generatedAt;
      if (!iso) return "";
      const d = new Date(iso);
      return `${dayLabel(dubaiDayStr(iso))} ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Dubai" })} Dubai`;
    });
    const tabs = computed(() => [
      { key: "OPEN", label: "Open", count: rows.value.length - grouped.value.lapsed.length },
      { key: "LAPSED", label: "Lapsed", count: grouped.value.lapsed.length },
      { key: "ACCEPTED", label: "Accepted" },
      { key: "DISMISSED", label: "Dismissed" },
      { key: "LOST", label: "Lost customers" },
    ]);

    onMounted(load);
    watch(tab, load);
    watch(() => store.state.currentSite, () => {
      selected.value = new Set();
      load();
    });

    return {
      rows, historyRows, lostRows, selected, tab, tabs, loading, error, summary, grouped, overdueCount, generatedLabel,
      load, removeRow, toggleSelect, pointLabel, datePart, dayLabel,
      dismissLabel: (r) => DISMISS_LABELS[r] || r || "",
      busyIds, bulkBusy, dismissing, dismissOptions, losing, lostBusy,
      accept, acceptSelected, openDismiss, dismiss, openLost, confirmLost, openEdit,
      editing, editPrefill, onEditedCreated,
    };
  },
};
</script>
